import React, { useEffect, useRef, useState } from 'react';
import { ACCENT, TEXT_DARK, TEXT_LIGHT, FONT } from '../lib/tokens';
import { Ic } from './Icons';
import { useSchedule } from '../lib/useSchedule';
import { getAccessToken } from './AccessGate';
import { useBreakpoint } from '../lib/useBreakpoint';

const EMPTY_STATE_PROMPTS = [
  'Summarize the key topics in today’s sessions.',
  'Help me draft a succession framework for my association.',
  'What does the pre-conference checklist ask me to bring?',
];

// Conversation history lives in sessionStorage (per-tab, cleared on tab
// close). Capped at MAX_HISTORY turns sent to the model so the system prompt
// + reference materials don't get crowded out.
const STORAGE_KEY = 'elx_ai_messages';
const MAX_HISTORY = 20;

function loadStored() {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export default function AIDrawer({ open, onClose }) {
  const [messages, setMessages] = useState(loadStored);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const { current } = useSchedule();
  const bp = useBreakpoint();
  const isDesktop = bp === 'desktop';

  // Mirror state to sessionStorage so accidental reloads don't lose the chat.
  useEffect(() => {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
      // Quota exceeded or storage disabled — non-fatal.
    }
  }, [messages]);

  const newConversation = () => {
    setMessages([]);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  const copyMessage = (text, idx) => {
    if (!text || !navigator.clipboard) return;
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx((cur) => (cur === idx ? null : cur)), 2000);
  };

  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current.focus(), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const q = text.trim();
    if (!q || loading) return;
    setInput('');

    const next = [...messages, { role: 'user', text: q }];
    setMessages(next);
    setLoading(true);

    // Cap how far back we send to the model so reference materials in the
    // system prompt always have room. UI keeps the full conversation visible.
    const historyForApi = next.slice(-MAX_HISTORY);

    try {
      const accessToken = getAccessToken();
      const res = await fetch('/api/ai-query', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          messages: historyForApi,
          sessionId: current?.id ?? null,
          sessionTitle: current?.title ?? null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { reply } = await res.json();
      const now = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      setMessages((prev) => [...prev, { role: 'ai', text: reply, time: now }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          text: "I'm having trouble connecting right now. Please try again in a moment.",
          time: '--',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith('**') && p.endsWith('**') ? (
        <strong key={i}>{p.slice(2, -2)}</strong>
      ) : (
        p.split('\n').map((line, j, arr) => (
          <React.Fragment key={`${i}-${j}`}>
            {line}
            {j < arr.length - 1 && <br />}
          </React.Fragment>
        ))
      ),
    );
  };

  // Layout differs between mobile (bottom sheet) and desktop (right panel),
  // but the panel-body content (header, messages, input) is the same.
  const panelStyle = isDesktop
    ? {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 420,
        zIndex: 50,
        background: '#fff',
        borderLeft: '1px solid #e2e8f0',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.25)',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
      }
    : {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '72%',
        zIndex: 50,
        background: '#fff',
        borderRadius: '20px 20px 0 0',
        transform: open ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.25)',
      };

  const backdropStyle = isDesktop
    ? {
        position: 'fixed',
        inset: 0,
        background: 'rgba(10,16,32,0.35)',
        zIndex: 40,
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'auto' : 'none',
        transition: 'opacity 0.25s ease',
      }
    : {
        position: 'absolute',
        inset: 0,
        background: 'rgba(10,16,32,0.55)',
        zIndex: 40,
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'auto' : 'none',
        transition: 'opacity 0.25s ease',
      };

  return (
    <>
      <div onClick={onClose} style={backdropStyle} />

      <div style={{ ...panelStyle, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px 12px',
            borderBottom: '1px solid #f1f5f9',
            background: '#fff',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: ACCENT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Ic.Spark size={16} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK, lineHeight: 1.2 }}>
                Ask ELX AI
              </div>
              <div style={{ fontSize: 11, color: TEXT_LIGHT, marginTop: 1 }}>
                Powered by session materials
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {messages.length > 0 && (
              <button
                onClick={newConversation}
                disabled={loading}
                aria-label="Start a new conversation"
                title="Start a new conversation"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '6px 10px',
                  borderRadius: 8,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: TEXT_DARK,
                  fontSize: 11.5,
                  fontWeight: 600,
                  fontFamily: FONT,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1,
                }}
              >
                + New
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close AI drawer"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Ic.Close size={16} color="#64748b" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {messages.length === 0 && !loading && (
            <EmptyState onPick={(t) => sendMessage(t)} />
          )}

          {messages.map((m, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              {m.role === 'ai' && (
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: ACCENT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginRight: 8,
                    alignSelf: 'flex-end',
                    marginBottom: 2,
                  }}
                >
                  <Ic.Spark size={13} color="#fff" />
                </div>
              )}
              <div
                style={{
                  maxWidth: '82%',
                  background: m.role === 'user' ? ACCENT : '#f8fafc',
                  border: m.role === 'ai' ? '1px solid #e2e8f0' : 'none',
                  borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  padding: '10px 13px',
                }}
              >
                <div
                  style={{
                    fontSize: 13.5,
                    color: m.role === 'user' ? '#fff' : TEXT_DARK,
                    lineHeight: 1.55,
                  }}
                >
                  {formatText(m.text)}
                </div>
                {(m.time || m.role === 'ai') && (
                  <div
                    style={{
                      marginTop: 5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: m.role === 'user' ? 'flex-end' : 'space-between',
                      gap: 8,
                    }}
                  >
                    {m.role === 'ai' && (
                      <button
                        onClick={() => copyMessage(m.text, i)}
                        aria-label="Copy reply"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '3px 8px',
                          borderRadius: 6,
                          background: copiedIdx === i ? '#dcfce7' : 'transparent',
                          border: `1px solid ${copiedIdx === i ? '#86efac' : '#e2e8f0'}`,
                          color: copiedIdx === i ? '#16a34a' : '#64748b',
                          fontSize: 10.5,
                          fontWeight: 600,
                          fontFamily: FONT,
                          cursor: 'pointer',
                        }}
                      >
                        {copiedIdx === i ? (
                          <Ic.Check size={10} color="#16a34a" />
                        ) : (
                          <Ic.Copy size={10} color="#64748b" />
                        )}
                        {copiedIdx === i ? 'Copied' : 'Copy'}
                      </button>
                    )}
                    {m.time && (
                      <span
                        style={{
                          fontSize: 10.5,
                          color: m.role === 'user' ? 'rgba(255,255,255,0.55)' : TEXT_LIGHT,
                        }}
                      >
                        {m.time}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: ACCENT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Ic.Spark size={13} color="#fff" />
              </div>
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '16px 16px 16px 4px',
                  padding: '12px 16px',
                }}
              >
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  {[0, 1, 2].map((j) => (
                    <div
                      key={j}
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: ACCENT,
                        animation: `elxpulse 1.2s ease-in-out ${j * 0.2}s infinite`,
                        opacity: 0.7,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            padding: '12px 16px 18px',
            borderTop: '1px solid #f1f5f9',
            background: '#fff',
            flexShrink: 0,
          }}
        >
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
            placeholder="Ask about sessions, strategy, AI tools…"
            disabled={loading}
            style={{
              flex: 1,
              padding: '10px 13px',
              border: '1.5px solid #e2e8f0',
              borderRadius: 10,
              fontSize: 13.5,
              color: TEXT_DARK,
              fontFamily: FONT,
              outline: 'none',
              background: loading ? '#f8fafc' : '#fff',
            }}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            aria-label="Send"
            style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              flexShrink: 0,
              background: input.trim() && !loading ? ACCENT : '#e2e8f0',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
              transition: 'background 0.15s',
            }}
          >
            <Ic.Send size={16} color={input.trim() && !loading ? '#fff' : '#94a3b8'} />
          </button>
        </div>
      </div>
    </>
  );
}

function EmptyState({ onPick }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        padding: '20px 6px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: ACCENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 14,
        }}
      >
        <Ic.Spark size={22} color="#fff" />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK, marginBottom: 5 }}>
        How can I help?
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: TEXT_LIGHT,
          lineHeight: 1.5,
          maxWidth: 280,
          marginBottom: 16,
        }}
      >
        Ask anything about the sessions, your pre-conference checklist, or the prompts shared in
        Resources.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', maxWidth: 320 }}>
        {EMPTY_STATE_PROMPTS.map((p) => (
          <button
            key={p}
            onClick={() => onPick(p)}
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              padding: '10px 13px',
              color: TEXT_DARK,
              fontSize: 12.5,
              fontFamily: FONT,
              textAlign: 'left',
              cursor: 'pointer',
              lineHeight: 1.4,
            }}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
